<template>
  <!-- Edit Device Dialog -->
  <q-dialog :model-value="modelValue" persistent @update:model-value="$emit('update:modelValue', $event)">
    <q-card style="width: 600px; max-width: 80vw;">
      <q-card-section>
        <div class="text-h6">Edit Device</div>
      </q-card-section>

      <q-card-section>
        <q-form @submit.prevent="handleSubmit">
          <!-- Template Selection at the TOP for consistency -->
          <q-select
            v-if="!isTemplatedDevice({ data: formData })"
            :model-value="formData.device_template_id"
            :options="deviceTemplates"
            label="Change Device Template (Optional)"
            filled
            emit-value
            map-options
            clearable
            class="q-mb-md"
            hint="Select a different template to update device details"
            @update:model-value="handleTemplateSelect"
          >
            <template #prepend>
              <q-icon name="inventory_2" color="primary" />
            </template>
          </q-select>

          <!-- Separator between template and details -->
          <q-separator v-if="!isTemplatedDevice({ data: formData })" class="q-my-md" />
          <div class="text-subtitle2 q-mb-md">Device Details</div>

          <!-- Device Details below template selection -->
          <q-input
            :model-value="formData.name"
            label="Device Name"
            filled
            required
            class="q-mb-md"
            @update:model-value="$emit('update:name', $event)"
          />
          <q-select
            :model-value="formData.brick_class"
            :options="deviceBrickClasses"
            label="Device Type (BRICK Class)"
            filled
            required
            emit-value
            map-options
            class="q-mb-md"
            :disable="isTemplatedDevice({ data: formData })"
            :hint="brickClassHint"
            @update:model-value="$emit('update:brick-class', $event)"
          />
          <q-input
            :model-value="formData.serial_number"
            label="Serial Number"
            filled
            class="q-mb-md"
            hint="Unique serial number for this installed device instance"
            clearable
            @update:model-value="$emit('update:serial-number', $event)"
          />

          <!-- Device Part Registry -->
          <q-separator class="q-my-md" />
          <div class="text-subtitle2 q-mb-md">
            <q-icon name="inventory" class="q-mr-xs" />
            Device Part (Optional)
          </div>

          <q-select
            v-model="selectedDevicePart"
            :options="partOptionsWithAdd"
            label="Search Device Part"
            filled
            use-input
            clearable
            option-label="display"
            hint="Search by part number, manufacturer, or model"
            class="q-mb-md"
            @filter="filterParts"
            @update:model-value="handlePartSelected"
          >
            <template #prepend>
              <q-icon name="search" />
            </template>

            <template #no-option>
              <q-item>
                <q-item-section class="text-grey">
                  Type 2+ characters to search...
                </q-item-section>
              </q-item>
            </template>

            <template #option="scope">
              <q-item v-bind="scope.itemProps">
                <q-item-section v-if="scope.opt.isAddNew">
                  <q-item-label>
                    <q-icon name="add_circle" color="primary" class="q-mr-sm" />
                    {{ scope.opt.display }}
                  </q-item-label>
                </q-item-section>
                <q-item-section v-else>
                  <q-item-label>{{ scope.opt.part_number }}</q-item-label>
                  <q-item-label caption>{{ scope.opt.manufacturer }} - {{ scope.opt.model }}</q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </q-select>

          <!-- Selected Device Part Info Card -->
          <q-card v-if="selectedDevicePart" flat bordered class="q-mb-md bg-blue-1">
            <q-card-section class="q-pa-md">
              <div class="row items-center justify-between q-mb-sm">
                <div class="text-subtitle2">Selected Part Details</div>
                <q-btn
                  flat
                  dense
                  color="primary"
                  icon="edit"
                  label="Edit Part in Registry"
                  size="sm"
                  @click="openEditPartInRegistry"
                >
                  <q-tooltip>Edit this part in the registry (affects all devices using this part)</q-tooltip>
                </q-btn>
              </div>
              <div class="row q-col-gutter-sm">
                <div class="col-6">
                  <div class="text-caption text-grey-7">Part Number</div>
                  <div class="text-weight-medium">{{ selectedDevicePart.part_number }}</div>
                </div>
                <div class="col-6">
                  <div class="text-caption text-grey-7">Manufacturer</div>
                  <div class="text-weight-medium">{{ selectedDevicePart.manufacturer }}</div>
                </div>
                <div class="col-6">
                  <div class="text-caption text-grey-7">Model</div>
                  <div class="text-weight-medium">{{ selectedDevicePart.model }}</div>
                </div>
                <div class="col-6">
                  <div class="text-caption text-grey-7">BRICK Class</div>
                  <div class="text-weight-medium">{{ selectedDevicePart.brick_class }}</div>
                </div>
              </div>
            </q-card-section>
          </q-card>

          <!-- Physical Location Section -->
          <q-separator class="q-my-md" />
          <div class="text-subtitle2 q-mb-md">Physical Location (Optional)</div>
          <div class="text-caption text-grey-7 q-mb-md">
            If this device is physically located in a different space than its equipment
          </div>
          <q-field filled label="Installation Location" stack-label class="q-mb-md" hint="Where is this device physically installed?">
            <template #control>
              <div class="full-width q-py-sm">
                <q-breadcrumbs v-if="deviceLocationPath.length > 0">
                  <q-breadcrumbs-el
                    v-for="(item, index) in deviceLocationPath"
                    :key="index"
                    :label="item.label"
                    :icon="item.icon"
                  />
                </q-breadcrumbs>
                <span v-else class="text-grey-6">Select location...</span>
              </div>
            </template>
            <template #append>
              <q-btn
                flat
                dense
                icon="edit_location"
                @click="$emit('open-location-picker')"
              >
                <q-tooltip>Change Location</q-tooltip>
              </q-btn>
              <q-btn
                v-if="formData.location_space_id"
                flat
                dense
                icon="clear"
                @click="$emit('clear-location')"
              >
                <q-tooltip>Clear Location</q-tooltip>
              </q-btn>
            </template>
          </q-field>
          <q-input
            :model-value="formData.location_description"
            label="Location Details"
            filled
            type="textarea"
            rows="2"
            class="q-mb-md"
            hint="e.g., 'North wall at 1.5m height', 'Above ceiling tile near window'"
            @update:model-value="$emit('update:location-description', $event)"
          />
        </q-form>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn flat label="Cancel" @click="$emit('update:modelValue', false)" />
        <q-btn color="primary" label="Update Device" :loading="saving" @click="handleSubmit" />
      </q-card-actions>
    </q-card>

    <!-- Add New Part Dialog (Shared Component) -->
    <AddDevicePartDialog
      v-model="showAddPartDialog"
      :part-number="addPartInitialData.partNumber"
      :brick-class="addPartInitialData.brickClass"
      @created="handlePartCreated"
    />

    <!-- Edit Device Part Dialog (Shared Component) -->
    <EditDevicePartDialog
      v-model="editPartDialogOpen"
      :part-id="editPartId"
      @updated="handlePartUpdated"
    />
  </q-dialog>
</template>

<script setup>
  import { ref, watch, computed } from 'vue';
  import { Notify } from 'quasar';
  import { authenticatedFetch } from '@/utils/apiHelper';
  import AddDevicePartDialog from './AddDevicePartDialog.vue';
  import EditDevicePartDialog from './EditDevicePartDialog.vue';

  const props = defineProps({
    modelValue: {
      type: Boolean,
      required: true,
    },
    formData: {
      type: Object,
      required: true,
    },
    deviceTemplates: {
      type: Array,
      default: () => [],
    },
    deviceBrickClasses: {
      type: Array,
      required: true,
    },
    deviceLocationPath: {
      type: Array,
      default: () => [],
    },
    saving: {
      type: Boolean,
      default: false,
    },
    isTemplatedDevice: {
      type: Function,
      required: true,
    },
    templateBrickClassConstraint: {
      type: String,
      default: null,
    },
  });

  const emit = defineEmits([
    'update:modelValue',
    'update:name',
    'update:brick-class',
    'update:serial-number',
    'update:device-part-id',
    'update:location-description',
    'template-select',
    'open-location-picker',
    'clear-location',
    'submit',
  ]);

  // Computed hint for brick class selector
  const brickClassHint = computed(() => {
    if (props.isTemplatedDevice({ data: props.formData })) {
      return 'Cannot change BRICK class for templated devices';
    }
    if (props.templateBrickClassConstraint) {
      // Extract readable name from brick:XXX format
      const constraintName = props.templateBrickClassConstraint
        .replace('brick:', '')
        .replace(/_/g, ' ');
      return `Filtered to "${constraintName}" and subclasses (from device template)`;
    }
    return '';
  });

  // Device Part state
  const selectedDevicePart = ref(null);
  const partOptions = ref([]);
  const currentSearchTerm = ref('');

  // Add New Part dialog state
  const showAddPartDialog = ref(false);
  const addPartInitialData = ref({
    partNumber: '',
    brickClass: '',
  });

  // Edit Part in Registry dialog state (Shared Component)
  const editPartDialogOpen = ref(false);
  const editPartId = ref(null);

  // Watch formData to initialize selected part
  watch(() => props.formData?.device_part, (devicePart) => {
    if (devicePart) {
      // Add display property for the part selector
      selectedDevicePart.value = {
        ...devicePart,
        display: `${devicePart.part_number} - ${devicePart.manufacturer} ${devicePart.model}`,
      };
    } else {
      selectedDevicePart.value = null;
    }
  }, { immediate: true });

  // Filter parts
  const filterParts = async (val, update, abort) => {
    currentSearchTerm.value = val;

    if (val.length < 2) {
      abort();
      return;
    }

    try {
      const params = new URLSearchParams({ q: val });
      const response = await authenticatedFetch(`/api/device-parts/search?${params}`);
      const data = await response.json();

      update(() => {
        partOptions.value = (data.data || []).map((part) => ({
          ...part,
          display: `${part.part_number} - ${part.manufacturer} ${part.model}`,
        }));
      });
    } catch (error) {
      abort();
    }
  };

  // Computed: Add "Add New" option
  const partOptionsWithAdd = computed(() => {
    const options = [...partOptions.value];

    if (currentSearchTerm.value?.length >= 2 && options.length === 0) {
      options.push({
        id: '__add_new__',
        isAddNew: true,
        display: `➕ Add "${currentSearchTerm.value}" as new part`,
        searchTerm: currentSearchTerm.value,
      });
    }

    return options;
  });

  // Handle part selection
  const handlePartSelected = (part) => {
    if (!part) {
      selectedDevicePart.value = null;
      emit('update:device-part-id', null);
      return;
    }

    if (part.isAddNew) {
      // Open add part dialog with pre-filled data
      addPartInitialData.value = {
        partNumber: part.searchTerm,
        brickClass: props.formData.brick_class || '',
      };
      showAddPartDialog.value = true;
      selectedDevicePart.value = null;
      return;
    }

    selectedDevicePart.value = part;
    emit('update:device-part-id', part?.id || null);
  };

  // Handle part created from shared dialog
  const handlePartCreated = (createdPart) => {
    // Add display property for the part selector
    const partWithDisplay = {
      ...createdPart,
      display: `${createdPart.part_number} - ${createdPart.manufacturer} ${createdPart.model}`,
    };

    // Auto-select the newly created part
    selectedDevicePart.value = partWithDisplay;
    emit('update:device-part-id', createdPart.id);
  };

  // Edit Part in Registry functions (Shared Component)
  const openEditPartInRegistry = () => {
    if (!selectedDevicePart.value || !selectedDevicePart.value.id) {return;}
    editPartId.value = selectedDevicePart.value.id;
    editPartDialogOpen.value = true;
  };

  const handlePartUpdated = (updatedPart) => {
    // Update the selected part with new data
    if (selectedDevicePart.value?.id === updatedPart.id) {
      selectedDevicePart.value = {
        ...updatedPart,
        display: `${updatedPart.part_number} - ${updatedPart.manufacturer} ${updatedPart.model}`,
      };
    }
  };

  const handleTemplateSelect = (templateId) => {
    emit('template-select', templateId);
  };

  const handleSubmit = () => {
    emit('submit');
  };
</script>
